import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';

import { getClass } from '../../../utils/component-helpers';

import Checkbox from '../checkbox';
import { Search } from '../search';
import Button from '../button';

import './dropdown.scss';

const DropdownMenu = props => {
  const {
    items,
    selected,
    onSearch,
    onSelect,
    menuAlign,
    isShowing,
    multi,
    searchable,
    confirmPrompt,
    onConfirm,
    onCancel,
    confirmText,
    cancelText,
    containerRef,
    matchToggleWidth,
    itemRenderer,
  } = props;

  const menuRef = useRef();
  const [menuHeight, setMenuHeight] = useState(0);

  //  Menu height for top alignment
  useEffect(() => {
    if (isShowing && menuRef.current && menuAlign.includes('top')) {
      setMenuHeight(menuRef.current.offsetHeight);
    }
  }, [isShowing]);

  const onItemSelect = value => {
    onSelect(value);
  };

  if (!isShowing || !items) return null;

  const content = (
    <div
      className={getClass('dropdown-menu-container', {
        'match-toggle': matchToggleWidth,
        left: menuAlign.includes('left'),
        right: menuAlign.includes('right'),
      })}
      style={{ top: menuAlign.includes('top') ? `-${menuHeight}px` : 'initial' }}
      ref={menuRef}
    >
      {searchable && (
        <Search
          placeholder="Search..."
          debounce={true}
          styleType="borderless"
          onSearch={onSearch}
        />
      )}
      <div className="item-list" ref={menuRef}>
        {items.map(({ label, value }) => {
          const isSelected =
            multi && selected
              ? selected.map(s => s.value).includes(value)
              : selected && selected.value === value;
          const itemClass = getClass('dropdown-option', { selected });

          return (
            <div key={value} className={itemClass} onClick={() => onItemSelect({ label, value })}>
              {multi && (
                <div className="checkbox-container">
                  <Checkbox checked={isSelected} />
                </div>
              )}
              {itemRenderer ? (
                itemRenderer({ label, value })
              ) : (
                <span className="item-label">{label}</span>
              )}
            </div>
          );
        })}
      </div>
      {confirmPrompt && (
        <div className="buttons">
          <Button onClick={onCancel} label={cancelText} size="small" kind="text" />
          <Button onClick={onConfirm} label={confirmText} size="small" kind="text" />
        </div>
      )}
    </div>
  );

  return ReactDOM.createPortal(content, containerRef.current);
};

DropdownMenu.propTypes = {
  // eslint-disable-next-line
  selected: PropTypes.arrayOf(PropTypes.any),
  items: PropTypes.arrayOf(PropTypes.shape({ label: PropTypes.string, value: PropTypes.any }))
    .isRequired,
  onSelect: PropTypes.func,
  menuAlign: PropTypes.oneOf(['left', 'right']),
  isShowing: PropTypes.bool,
  searchable: PropTypes.bool,
  confirmPrompt: PropTypes.bool,
  onConfirm: PropTypes.func,
  onCancel: PropTypes.func,
  confirmText: PropTypes.string,
  cancelText: PropTypes.string,
  matchToggleWidth: PropTypes.bool,
  itemRenderer: PropTypes.func,
};

DropdownMenu.defaultProps = {
  selected: [],
  onSelect: () => {},
  menuAlign: 'right',
  isShowing: false,
  searchable: false,
  confirmPrompt: false,
  onConfirm: () => {},
  onCancel: () => {},
  confirmText: 'Done',
  cancelText: 'Cancel',
  matchToggleWidth: false,
  itemRenderer: null,
};

export default DropdownMenu;
